import React from 'react';
import { motion } from "framer-motion";
import debounce from "lodash.debounce";
import { useCallback, useEffect, useState } from "react";
import { IoDiamond, IoSearch } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../Hooks/useAuth";
import homeHero from "../../../assets/HomeHero.svg";
import LocationsDropdown from "../../../components/locations/LocationsDropdown";
import MobileLocationSelector from "../../../components/locations/MobileLocationSelector";
import PromotionModal from "./PromotionModal";

const Hero = () => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const { user } = useAuth();

    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 100) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const debouncedSearch = useCallback((query: string) => {
        if (query.trim()) {
            navigate(`/search?query=${encodeURIComponent(query)}`);
        }
    }, [navigate]);

    const debouncedSearchWithDelay = debounce(debouncedSearch, 500);

    const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const query = e.target.value;
        setSearchQuery(query);
        debouncedSearchWithDelay(query);
    };

    const handleSearchSubmit = () => {
        if (searchQuery.trim()) {
            navigate(`/search?query=${encodeURIComponent(searchQuery)}`);
        }
    };

    // const popularSearches = ["Voitures", "Téléphones", "Immobilier", "Emploi"];

    return (
        <div className="relative">
            {/* Hero Section */}
            <div className={`bg-gradient-to-br from-accent-secondary via-accent-default to-accent-default relative overflow-hidden transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}>
                {/* Background Pattern */}
                <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>

                {/* Animated Circles */}
                <div className="absolute -top-24 -left-16 w-56 h-56 bg-white/10 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-24 -right-16 w-64 h-64 bg-amber-300/20 rounded-full blur-3xl"></div>

                <div className="container mx-auto px-4 md:px-16 py-4 md:py-10 relative">
                    <div className="items-center gap-8 lg:flex">
                        {/* Left Content */}
                        <motion.div
                            className="w-full flex-1"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <div className="lg:max-w-2xl">
                                 <motion.h1
                                    className="text-xl md:text-3xl lg:text-5xl font-bold text-white mb-2 md:mb-3 leading-tight"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.2 }}
                                >
                                    Falashop <span className="text-amber-400">🛒</span> La plateforme d'annonces la plus fiable
                                </motion.h1>

                                <motion.p
                                    // on phones the title says enough: this line is for bigger screens
                                    className="hidden md:block text-lg md:text-xl text-white/90 mb-4 max-w-lg"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.4 }}
                                >
                                    Trouvez rapidement ce que vous cherchez ou vendez facilement ce dont vous n'avez plus besoin.
                                </motion.p>

                                {/* Search Box */}
                                <motion.div
                                    className="bg-white p-1.5 rounded-xl shadow-lg flex flex-row items-center gap-1.5"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.6 }}
                                >
                                    <div className="flex items-center bg-gray-100 rounded-lg px-3 py-2 flex-1">
                                        <IoSearch className="text-primary-default mr-2 shrink-0" size={20} />
                                        <input
                                            className="flex-grow bg-transparent border-none focus:outline-none text-gray-800 placeholder-primary-tertiary"
                                            type="text"
                                            placeholder="Que cherchez-vous aujourd'hui?"
                                            value={searchQuery}
                                            onChange={handleSearchInputChange}
                                            onKeyDown={(e) => e.key === "Enter" && handleSearchSubmit()}
                                        />
                                    </div>
                                    <div className="hidden md:block md:w-56 [&_p]:truncate">
                                        <LocationsDropdown />
                                    </div>
                                    <button
                                        onClick={handleSearchSubmit}
                                        aria-label="Rechercher"
                                        className="bg-gradient-to-r from-primary-default to-primary-secondary hover:opacity-90 text-white font-semibold rounded-lg px-5 py-2.5 transition-all flex items-center justify-center gap-2"
                                    >
                                        <IoSearch size={18} />
                                        <span className="hidden sm:inline">Rechercher</span>
                                    </button>
                                </motion.div>

                                {/* Mobile Location Selector - Visible only on mobile */}
                                <div className="md:hidden mt-2">
                                    <MobileLocationSelector />
                                </div>

                                {/* Popular searches + Sell CTA */}
                                <motion.div
                                    className="mt-3 md:mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.8 }}
                                >
                                    
                                    {/* Call to Action */}
                                <motion.div
                                    className="flex flex-wrap gap-3 sm:gap-4"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.8 }}
                                >
                                    <button
                                        onClick={() => navigate('/sell')}
                                        className="bg-gradient-to-r from-primary-default to-primary-secondary hover:from-primary-secondary hover:to-primary-secondary text-white font-semibold py-3 px-4 sm:px-6 rounded-lg transition-all shadow-lg hover:shadow-primary-default/30 focus:ring-2 focus:ring-white focus:ring-opacity-50 flex items-center"
                                    >
                                        <span className="mr-2">Vendre Maintenant</span>
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                                        </svg>
                                    </button>
                                    {/* Plans & premium popup (on phones this is the way to it) */}
                                    <button
                                        onClick={() => setIsModalVisible(true)}
                                        className="bg-gradient-to-r from-amber-400 to-amber-500 hover:from-amber-500 hover:to-amber-600 text-white font-bold py-3 px-4 sm:px-5 rounded-lg transition-all shadow-lg hover:shadow-amber-500/30 focus:ring-2 focus:ring-amber-400 focus:ring-opacity-50 flex items-center gap-2"
                                    >
                                        <IoDiamond size={18} />
                                        {/* short on phones so both buttons fit on one line */}
                                        <span className="sm:hidden">VIP</span>
                                        <span className="hidden sm:inline">Devenir VIP</span>
                                    </button>
                                </motion.div>
                                </motion.div>
                            </div>
                        </motion.div>

                        {/* Right Content - Image */}
                        <motion.div
                            className="hidden lg:flex items-center justify-center lg:w-[34%] shrink-0 relative"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                        >
                            <img
                                className="w-full h-auto max-w-xs object-contain relative z-10"
                                src={homeHero}
                                alt="Falashop Marketplace"
                            />
                            <div className="absolute top-1/2 right-1/4 w-28 h-28 bg-amber-400/30 rounded-full filter blur-xl"></div>
                        </motion.div>
                    </div>
                </div>
            </div>

            <PromotionModal
                visible={isModalVisible}
                onClose={() => setIsModalVisible(false)}
                userId={user?.id}
            />
        </div>
    );
};

export default Hero;